import { useEffect, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useAppStore } from '@/store'
import { apiService } from '@/service/api'
import { formatRelativeTime } from '@/utils/timeUtils'
import { useTranslation } from '@/i18n'
import { toast } from 'sonner'

interface VersionHistoryPanelV2Props {
  isOpen: boolean
  graphId: string
  onSave: () => void
}

export default function VersionHistoryPanelV2({
  isOpen,
  graphId,
  onSave,
}: VersionHistoryPanelV2Props) {
  const { t } = useTranslation()
  const { versionHistory, setVersionHistory, setCurrentVersion, loadVersion } =
    useAppStore()
  const [hoveredVersion, setHoveredVersion] = useState<number | null>(null)
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen || !graphId) return
    const fetch = async () => {
      const history = await apiService.getVersionHistory(graphId)
      setVersionHistory(history)
      if (history.length > 0) {
        setCurrentVersion(history[0].version)
      }
    }
    fetch()
  }, [isOpen, graphId, setVersionHistory, setCurrentVersion])

  if (!isOpen) return null

  const currentVersion = versionHistory.currentVersion
  const versions = versionHistory.versions

  const handleRestore = async () => {
    if (restoreTarget === null) return
    try {
      await loadVersion(restoreTarget)
      onSave()
      // Refresh version history after save
      const history = await apiService.getVersionHistory(graphId)
      setVersionHistory(history)
      if (history.length > 0) {
        setCurrentVersion(history[0].version)
      }
      toast.success(
        t('data_catalog:graph_editor.version_history.restored', {
          defaultValue: 'Restored Successfully',
        })
      )
    } catch {
      toast.error(
        t('data_catalog:graph_editor.version_history.restore_failed', {
          defaultValue: 'Failed to restore version',
        })
      )
    } finally {
      setRestoreTarget(null)
    }
  }

  const formatTimestamp = (createdAt: string) => {
    try {
      return formatRelativeTime(createdAt)
    } catch {
      return new Date(createdAt).toLocaleString()
    }
  }

  return (
    <>
      <div className="w-[391px] shrink-0 border-l border-[#e5e5e5] bg-white dark:bg-dark-card dark:border-dark-border overflow-y-auto">
        <div className="p-4 flex flex-col gap-1">
          {versions.map(({ version, created_at }) => {
            const isCurrent = version === currentVersion
            return (
              <div
                key={version}
                className="flex items-center gap-1 p-2 rounded-lg min-w-[228px] w-full hover:bg-[#f5f5f5] dark:hover:bg-dark-muted transition-colors"
                onMouseEnter={() => setHoveredVersion(version)}
                onMouseLeave={() => setHoveredVersion(null)}
              >
                <div className="flex-1 flex flex-col gap-1 min-w-0 text-sm">
                  <p className="font-medium text-[#0a0a0a] dark:text-foreground leading-5">
                    Version {version}
                    {isCurrent && (
                      <span className="text-[#737373]"> (Current)</span>
                    )}
                  </p>
                  <p className="text-[#737373] leading-5 font-normal">
                    {formatTimestamp(created_at)}
                  </p>
                </div>
                {!isCurrent && hoveredVersion === version && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1 shrink-0"
                    onClick={() => setRestoreTarget(version)}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span className="text-sm font-medium">
                      {t('data_catalog:graph_editor.version_history.restore', {
                        defaultValue: 'Restore',
                      })}
                    </span>
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <AlertDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('data_catalog:graph_editor.version_history.restore_title', {
                defaultValue: `Restore Version ${restoreTarget}?`,
                version: restoreTarget,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'data_catalog:graph_editor.version_history.restore_description',
                {
                  defaultValue:
                    'A new version will be created with this content. Your version history will be preserved.',
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common:buttons.cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRestore}>
              {t('data_catalog:graph_editor.version_history.restore', {
                defaultValue: 'Restore',
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
